import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from '@mui/material';

/**
 * Reusable DataTable component with sticky header and consistent styling
 * @param {Object} props
 * @param {Array} props.columns - Column definitions: [{ key, label, width, align? }]
 * @param {Array} props.data - Array of row data objects
 * @param {Function} props.renderRow - Custom row renderer function (row, index, onEdit, ...args) => JSX
 * @param {Function} props.onEdit - Edit handler function
 * @param {string} props.minWidth - Min width for table (default: '800px')
 * @param {boolean} props.loading - Loading state
 * @param {string} props.emptyMessage - Message when no data
 * @param {Array} props.renderRowArgs - Additional arguments to pass to renderRow
 */
const DataTable = ({
  columns = [],
  data = [],
  renderRow,
  onEdit,
  minWidth = '800px',
  loading = false,
  emptyMessage = 'No data found.',
  renderRowArgs = [],
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '70vh', display: 'flex', flexDirection: 'column' }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '& .MuiTable-root': {
            tableLayout: 'fixed',
            width: '100%',
          }
        }}
      >
        <Table>
          <TableHead
            sx={{
              backgroundColor: '#1e293b',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              flexShrink: 0
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    width: column.width,
                    padding: '12px 6px',
                    borderBottom: '2px solid #374151',
                    textAlign: column.align || 'left',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ flex: 1 }}>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body1">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                if (renderRow) {
                  return renderRow(row, index, onEdit, ...renderRowArgs);
                }

                // Default row rendering
                return (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                      '&:hover': { backgroundColor: '#e2e8f0' },
                      '& .MuiTableCell-root': {
                        padding: '8px 6px',
                        overflow: 'visible',
                        whiteSpace: 'normal'
                      }
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          textAlign: column.align || 'left',
                        }}
                      >
                        {row[column.key] || 'N/A'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataTable;